import { TemplatePortal } from '@angular/cdk/portal';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { ServiceRegistryService } from 'src/app/components/services/service-registry/services/service-registry.service';
import { AppService } from 'src/app/services/app.service';
import { UserService } from 'src/app/services/user.service';

export interface HelpboxOverlay {
	title: string;
	content: string;
}

export interface ServiceInformation {
	name: string;
	displayName: string;
	path: string;
	isExternal?: boolean;
	disabled?: boolean;
}

export interface AnchorInformation {
	displayName: string;
	element: HTMLElement;
}

export interface NavigationInformation {
	title?: string;
	overview: ServiceInformation[];
	sections: {
		name: string;
		resources: ServiceInformation[];
		disabled?: boolean;
	}[];
}

@Component({
	selector: 'mcm-default-service-layout[nav]',
	templateUrl: './default-service-layout.component.html',
	styleUrls: ['./default-service-layout.component.scss'],
	standalone: false,
})
export class DefaultServiceLayoutComponent implements AfterViewInit, OnDestroy {
	@Input() nav!: NavigationInformation;

	public selectedService?: string = undefined;
	public selectedServiceEnabled: boolean = true;
	public helpOpen: boolean = false;
	public anchors: AnchorInformation[] = [];

	protected width: string = '350px';
	protected title?: string;
	protected helpboxOverlay?: HelpboxOverlay;
	protected templatePortal?: TemplatePortal<any> = undefined;

	private mutationObserver?: MutationObserver;
	private routerOutletElement?: HTMLElement;

	@Output() helpBoxClosed = new EventEmitter<void>();

	constructor(
		private appService: AppService,
		private userService: UserService,
		private serviceRegistryService: ServiceRegistryService,
		private router: Router,
		private _viewContainerRef: ViewContainerRef,
		private elementRef: ElementRef
	) {
		this.appService.currentService.pipe(takeUntilDestroyed()).subscribe(async (currentService) => {
			if (currentService) {
				this.selectedService = currentService;
				const service = (await this.serviceRegistryService.getAvailableServices()).find(
					(availableService) => availableService.displayName === currentService
				);
				if (service?.serviceName) {
					this.selectedServiceEnabled = await this.userService.getServiceState(service?.serviceName);
				}
			}
		});

		this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.resetHelpbox();
				setTimeout(() => this.collectAnchors(), 100);
			}
		});
	}

	ngAfterViewInit() {
		this.routerOutletElement = this.elementRef.nativeElement.querySelector('router-outlet');
		setTimeout(() => this.collectAnchors(), 100);
		this.setupMutationObserver();
	}

	ngOnDestroy() {
		this.mutationObserver?.disconnect();
	}

	private setupMutationObserver() {
		if (!this.routerOutletElement) return;

		this.mutationObserver = new MutationObserver(() => {
			setTimeout(() => this.collectAnchors(), 100);
		});

		this.mutationObserver.observe(this.routerOutletElement.parentElement!, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['data-anchor'],
		});
	}

	private collectAnchors() {
		this.anchors = [];
		if (!this.routerOutletElement) return;

		const anchorElements = this.elementRef.nativeElement.querySelectorAll('[data-anchor]');

		anchorElements.forEach((element: HTMLElement, index: number) => {
			const anchorValue = element.getAttribute('data-anchor');

			const displayName =
				anchorValue?.trim() ||
				element.querySelector('h1, h2, h3, h4, h5, h6, .editor-header')?.textContent?.trim() ||
				`Section ${index + 1}`;

			this.anchors.push({
				displayName: displayName,
				element: element,
			});
		});
	}

	public scrollToAnchor(anchor: AnchorInformation) {
		anchor.element.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	}

	public updateHelpBox(title: string, description: string, open: boolean = true) {
		this.helpboxOverlay = {
			title,
			content: description,
		};
		this.title = title;

		if (open) {
			this.helpOpen = true;
		}
	}

	public updateTemplatePortal(content: any, title?: string, open: boolean = true) {
		this.templatePortal = new TemplatePortal(content, this._viewContainerRef);
		this.title = title;

		if (open) {
			this.helpOpen = true;
		}
	}

	public toggleHelp() {
		this.helpOpen = !this.helpOpen;
		this.helpboxOverlay = undefined;
		this.templatePortal = undefined;

		if (!this.helpOpen) {
			this.helpBoxClosed.emit();
		}
	}

	private resetHelpbox() {
		this.helpOpen = false;
		this.helpboxOverlay = undefined;
		this.templatePortal = undefined;
	}
}
